import { RateLimitError } from "../errors.js";
import type { HandlerContext, HandlerUtility, RateLimitOptions } from "../types.js";

/**
 * In-memory store for rate limiting.
 * For production, use Redis or another distributed store.
 */
const store = new Map<string, { count: number; resetAt: number }>();

/**
 * Clean up expired entries periodically.
 */
function cleanupStore(): void {
    const now = Date.now();
    for (const [key, value] of store) {
        if (value.resetAt < now) {
            store.delete(key);
        }
    }
}

// Run cleanup every minute
setInterval(cleanupStore, 60_000);

/**
 * Default key generator using IP address.
 */
function defaultKeyGenerator(context: HandlerContext): string {
    const forwarded = context.request.headers.get("x-forwarded-for");
    const ip = forwarded?.split(",")[0]?.trim() ?? "unknown";
    return `rate-limit:${ip}`;
}

/**
 * Rate limiting utility to prevent abuse.
 *
 * @example
 * ```ts
 * import { compose, withRateLimit } from "next-route-compose";
 *
 * // 100 requests per minute
 * export const GET = compose(
 *   withRateLimit({ limit: 100, window: 60 })
 * )(handler);
 *
 * // Custom key generator (e.g., by user ID)
 * export const POST = compose(
 *   withRateLimit({
 *     limit: 10,
 *     window: 60,
 *     keyGenerator: (ctx) => `user:${ctx.user?.id ?? 'anonymous'}`
 *   })
 * )(handler);
 * ```
 */
export function withRateLimit(options: RateLimitOptions): HandlerUtility {
    const {
        limit,
        window,
        keyGenerator = defaultKeyGenerator,
        onRateLimited,
    } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            const key = await keyGenerator(context);
            const now = Date.now();
            const windowMs = window * 1000;

            // Get or create rate limit entry
            let entry = store.get(key);

            if (!entry || entry.resetAt < now) {
                // New window
                entry = { count: 0, resetAt: now + windowMs };
                store.set(key, entry);
            }

            // Increment count
            entry.count++;

            // Calculate remaining and reset time
            const remaining = Math.max(0, limit - entry.count);
            const reset = Math.ceil(entry.resetAt / 1000);
            const retryAfter = Math.ceil((entry.resetAt - now) / 1000);

            // Check if rate limited
            if (entry.count > limit) {
                if (onRateLimited) {
                    return await onRateLimited(context);
                }
                return new RateLimitError("Too many requests", retryAfter).toResponse();
            }

            // Call handler and add rate limit headers
            const response = await handler(context);

            const newHeaders = new Headers(response.headers);
            newHeaders.set("X-RateLimit-Limit", String(limit));
            newHeaders.set("X-RateLimit-Remaining", String(remaining));
            newHeaders.set("X-RateLimit-Reset", String(reset));

            return new Response(response.body, {
                status: response.status,
                statusText: response.statusText,
                headers: newHeaders,
            });
        };
    };
}

/**
 * Create a custom rate limiter with a different store.
 * Useful for distributed systems using Redis or other backends.
 *
 * @example
 * ```ts
 * const redisRateLimit = createRateLimiter({
 *   get: async (key) => await redis.get(key),
 *   set: async (key, value, ttl) => await redis.setex(key, ttl, value),
 *   increment: async (key) => await redis.incr(key),
 * });
 * ```
 */
export interface RateLimitStore {
    get: (key: string) => Promise<{ count: number; resetAt: number } | null>;
    set: (key: string, value: { count: number; resetAt: number }) => Promise<void>;
}

export function createRateLimiter(customStore: RateLimitStore) {
    return function withCustomRateLimit(options: RateLimitOptions): HandlerUtility {
        const {
            limit,
            window,
            keyGenerator = defaultKeyGenerator,
            onRateLimited,
        } = options;

        return (handler) => {
            return async (context: HandlerContext) => {
                const key = await keyGenerator(context);
                const now = Date.now();
                const windowMs = window * 1000;

                let entry = await customStore.get(key);

                if (!entry || entry.resetAt < now) {
                    entry = { count: 1, resetAt: now + windowMs };
                } else {
                    entry.count++;
                }

                await customStore.set(key, entry);

                const remaining = Math.max(0, limit - entry.count);
                const reset = Math.ceil(entry.resetAt / 1000);
                const retryAfter = Math.ceil((entry.resetAt - now) / 1000);

                if (entry.count > limit) {
                    if (onRateLimited) {
                        return await onRateLimited(context);
                    }
                    return new RateLimitError("Too many requests", retryAfter).toResponse();
                }

                const response = await handler(context);

                const newHeaders = new Headers(response.headers);
                newHeaders.set("X-RateLimit-Limit", String(limit));
                newHeaders.set("X-RateLimit-Remaining", String(remaining));
                newHeaders.set("X-RateLimit-Reset", String(reset));

                return new Response(response.body, {
                    status: response.status,
                    statusText: response.statusText,
                    headers: newHeaders,
                });
            };
        };
    };
}
